/**
 * Utility functions for Solar Design page
 * Extracted from SolarDesignPage.js for maintainability
 */

export function distLatLng(a, b) {
  const dlat = (b.lat - a.lat) * 111320;
  const dlng = (b.lng - a.lng) * 111320 * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  return Math.sqrt(dlat * dlat + dlng * dlng);
}

export function fmtMM(metres) {
  const mm = Math.round(metres * 1000);
  return `${mm} mm`;
}

// Compass bearing (deg from North) of segment a→b
export function bearingDeg(a, b) {
  const DEG = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * DEG) * Math.cos(b.lat * DEG);
  const x = Math.cos(a.lat * DEG) * Math.sin(b.lat * DEG) -
            Math.sin(a.lat * DEG) * Math.cos(b.lat * DEG) * Math.cos((b.lng - a.lng) * DEG);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function toXY(p, ref) {
  const DEG = Math.PI / 180;
  return { x: (p.lng - ref.lng) * 111320 * Math.cos(ref.lat * DEG), y: (p.lat - ref.lat) * 111320 };
}

// Perpendicular distance (metres) from point p to segment a-b
export function pointToSegMeters(p, a, b) {
  const P = toXY(p, a), A = toXY(a, a), B = toXY(b, a);
  const dx = B.x - A.x, dy = B.y - A.y;
  const l2 = dx * dx + dy * dy || 1e-9;
  let t = ((P.x - A.x) * dx + (P.y - A.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  const cx = A.x + t * dx, cy = A.y + t * dy;
  return Math.hypot(P.x - cx, P.y - cy);
}

// Index of the polygon edge (i → i+1) nearest to a click point
export function nearestEdgeIndex(pts, click) {
  let best = 0, bestD = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const d = pointToSegMeters(click, a, b);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

export function createEdgeLabel(L, a, b, color) {
  const midLat = (a.lat + b.lat) / 2;
  const midLng = (a.lng + b.lng) / 2;
  const d = distLatLng(a, b);
  const label = fmtMM(d);
  return L.marker([midLat, midLng], {
    icon: L.divIcon({
      className: "",
      html: `<div style="background:${color};color:#fff;font-size:10px;font-weight:700;padding:1px 5px;border-radius:3px;white-space:nowrap;font-family:Manrope,sans-serif;pointer-events:none;transform:translate(-50%,-50%)">${label}</div>`,
      iconSize: [0, 0],
    }),
    interactive: false,
  });
}

// ── Typed dimensions ────────────────────────────────────────────────────────

// Move `metres` from point p along compass `bearing` (deg from North, clockwise)
export function destPoint(p, bearing, metres) {
  const DEG = Math.PI / 180;
  const dN = metres * Math.cos(bearing * DEG);
  const dE = metres * Math.sin(bearing * DEG);
  return {
    lat: p.lat + dN / 111320,
    lng: p.lng + dE / (111320 * Math.cos(p.lat * DEG)),
  };
}

/**
 * Rectangle from typed dimensions. `anchor` is the first corner; `widthM` runs
 * along `bearing`, `lengthM` along bearing+90 (clockwise). Returns 4 lat/lng points.
 */
export function rectangleFromDims(anchor, widthM, lengthM, bearing = 90) {
  const b = destPoint(anchor, bearing, widthM);
  const c = destPoint(b, bearing + 90, lengthM);
  const d = destPoint(anchor, bearing + 90, lengthM);
  return [ { ...anchor }, b, c, d ];
}

/** Centre a rectangle of the given size on `center` with the given bearing. */
export function rectangleCentredOn(center, widthM, lengthM, bearing = 90) {
  const back = destPoint(center, bearing + 180, widthM / 2);
  const anchor = destPoint(back, bearing + 270, lengthM / 2);
  return rectangleFromDims(anchor, widthM, lengthM, bearing);
}

/** Edge lengths (m) and bearings of a closed polygon. */
export function edgeStats(pts) {
  return pts.map((a, i) => {
    const b = pts[(i + 1) % pts.length];
    return { idx: i, length: distLatLng(a, b), bearing: bearingDeg(a, b) };
  });
}

/**
 * Set edge i→i+1 to `newLenM` by sliding vertex i+1 along the edge direction.
 * The following vertex (i+2) is shifted by the same delta so a rectangle stays
 * a rectangle (its opposite edge moves in parallel). Other vertices are untouched.
 */
export function resizeEdge(pts, i, newLenM) {
  const n = pts.length;
  const a = pts[i], b = pts[(i + 1) % n];
  const cur = distLatLng(a, b);
  if (cur < 1e-6 || !(newLenM > 0)) return pts;
  const brg = bearingDeg(a, b);
  const nb = destPoint(a, brg, newLenM);
  const dLat = nb.lat - b.lat, dLng = nb.lng - b.lng;
  const out = pts.map(p => ({ ...p }));
  out[(i + 1) % n] = nb;
  if (n >= 4) {
    const j = (i + 2) % n;
    out[j] = { lat: pts[j].lat + dLat, lng: pts[j].lng + dLng };
  }
  return out;
}

/** True if the polygon is (close to) an axis-agnostic rectangle. */
export function isRectangle(pts, tolM = 0.15) {
  if (pts.length !== 4) return false;
  const e = edgeStats(pts);
  const okLen = Math.abs(e[0].length - e[2].length) < tolM && Math.abs(e[1].length - e[3].length) < tolM;
  const ang = (x, y) => { const d = Math.abs(((x - y) % 360 + 360) % 360); return Math.min(d, 360 - d); };
  const okAng = Math.abs(ang(e[0].bearing, e[1].bearing) - 90) < 3 && Math.abs(ang(e[1].bearing, e[2].bearing) - 90) < 3;
  return okLen && okAng;
}
